import { Channel } from '@pasqal/core/ui/components/pulser-studio/components/Channel';
import { useContextMenu } from '~/systems/contextMenu';
import { type ComputedPulse, useExperiment } from '~/systems/experiment';
import { useUiState } from '~/systems/uiState';
import { PulseInteractive } from '../Pulse';
import type { Ref } from 'react';
import type { Channel as ChannelType } from '~/models/studio/channel';

interface ChannelInteractiveProps {
  ref?: Ref<HTMLElement>;
  totalTime: number;
  timeScale: number;
  tickStep: number;
  legendWidth: number;
  channel: ChannelType;
  computedPulses: ComputedPulse[];
  channelHeight: number;
  onUpdateChannelHeight: (val: number) => void;
}

export const ChannelInteractive = ({
  ref,
  totalTime,
  timeScale,
  tickStep,
  legendWidth,
  channel,
  computedPulses,
  channelHeight,
  onUpdateChannelHeight
}: ChannelInteractiveProps) => {
  const { atoms, commitedAtoms, createNewPulse } = useExperiment();
  const uiState = useUiState();
  const menu = useContextMenu();

  const handleContextMenu = () => {
    menu.registerOnce({
      label: 'Add Pulse',
      action: handleAddPulse
    });
  };

  const canAddPulse = commitedAtoms.length !== 0;

  const handleAddPulse = () => {
    const pulseUuid = createNewPulse(channel.uuid);
    if (pulseUuid) {
      uiState.selectPulse(pulseUuid);
    }
  };

  function selectChannel() {
    uiState.selectChannel(channel.uuid);
  }

  function deselectChannel() {
    uiState.selectChannel(null);
  }

  function toggleChannelSelection() {
    if (uiState.selectedChannel === channel.uuid) {
      deselectChannel();
    } else {
      selectChannel();
    }
  }

  const isSelected = uiState.selectedChannel === channel.uuid;

  const interactivity = {
    isSelected,
    deselectChannel,
    selectChannel,
    toggleChannelSelection,
    onAddPulse: canAddPulse ? handleAddPulse : undefined,
    onContextMenu: handleContextMenu
  };

  return (
    <Channel
      ref={ref}
      pulseComponent={PulseInteractive}
      totalTime={totalTime}
      timeScale={timeScale}
      tickStep={tickStep}
      legendWidth={legendWidth}
      channel={channel}
      computedPulses={computedPulses}
      channelHeight={channelHeight}
      atoms={atoms}
      interactivity={interactivity}
      onUpdateChannelHeight={onUpdateChannelHeight}
    />
  );
};
